import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { IndexingProgress } from '@localagent/sdk';
import { applyIndexingProgressAction } from '@localagent/sdk/react';
import { bridgeEventStreamAtom } from '../stores/sdk/atoms';
import { applyWorkspaceProjectionAction } from '../stores/workspace/projection';
import { useShogunchop } from '../providers/ShogunChopProvider';

/** Keep live progress presentational and request canonical projection refreshes (app-shell ADR-009). */
export function useIndexingProgressStream(): void {
  const stream = useAtomValue(bridgeEventStreamAtom);
  const shogunchop = useShogunchop();
  const applyProgress = useSetAtom(applyIndexingProgressAction);
  const applyProjection = useSetAtom(applyWorkspaceProjectionAction);

  useEffect(() => {
    const apply = (progress: IndexingProgress) => {
      applyProgress(progress);
      applyProjection({
        type: 'index_progress_received',
        workspaceId: progress.workspaceId,
        emitterEpoch: progress.emitterEpoch,
        indexRevision: progress.indexRevision,
      });
    };

    const sc = shogunchop.resource;
    if (!sc) {
      if (!stream) return;
      return stream.onIndexingProgress(apply);
    }

    const relay = sc.sharedEvents();

    relay.on((event) => {
      const e = event as { event?: string; data?: Record<string, unknown> };
      if (e.event !== 'search.indexingProgress' || !e.data) return;
      const w = e.data as {
        account_id: string;
        workspace_id: string;
        queued: number;
        embedding: number;
        indexed: number;
        ready?: boolean;
        progress: 'idle' | 'indexing';
        health: 'ok' | 'degraded';
        emitter_epoch: number;
        index_revision: number;
      };
      apply({
        accountId: w.account_id,
        workspaceId: w.workspace_id,
        queued: w.queued,
        embedding: w.embedding,
        indexed: w.indexed,
        ready: w.ready === true,
        progress: w.progress,
        health: w.health,
        emitterEpoch: w.emitter_epoch,
        indexRevision: w.index_revision,
      });
    });

    return () => {
      relay.release();
    };
  }, [applyProgress, applyProjection, shogunchop.resource, stream]);
}
